/**
 * Экран «Поля» — список с фильтрами.
 *
 * AK-750 (заказ founder 25.08): фильтры экрана живут В АДРЕСЕ, а имя поля —
 * настоящая ссылка. Сценарий дословно: «открыл, отфильтровал нужные
 * хозяйства, открыл поле, нажал назад — фильтры сохраняются». Ни возврат, ни
 * перезагрузка, ни пересылка ссылки коллеге не работали, пока фильтры лежали
 * в `useState`. Схема адреса и разбор значений — `./listSearch`.
 *
 * AK-772 (решение founder 28.08): выбранное в правой панели хозяйство —
 * УМОЛЧАНИЕ экрана. До этого «Поля» про глобальную область не знали вовсе:
 * человек выбирал в панели «Аргамак», открывал «Поля» и получал все 7 547
 * полей холдинга; список ручку `/fields` по области не сужает и на её смену
 * не реагировал. Гибрид ровно как в «Аналитике прошлых лет»
 * (`pages/analytics/history.tsx`) — три правила, и все три обязательны:
 *
 *   1. адрес пуст → фильтр берётся из области панели (хозяйство или кластер);
 *   2. выбор на самом экране (или `?farm_id=` из присланной ссылки) сильнее —
 *      он перекрывает умолчание, пока человек его не снял;
 *   3. смена области в панели сбрасывает выбор экрана и перезапрашивает
 *      список: раз умолчание завязано на область, оно обязано на неё и
 *      реагировать.
 *
 * Сам выбор ЛОКАЛЬНЫЙ — `POST /scope/set` отсюда не зовётся: заглянувший в
 * поля соседнего хозяйства не должен обнаружить, что у него переключилась
 * вся система.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  Building2,
  FileDown,
  FilterX,
  MapPin,
  Plus,
  Search,
  Settings2,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  VirtualTable,
  type SortState,
  type VirtualColumn,
} from '@/components/ui/virtual-table';
import { FieldsArchiveDialog } from '@/components/fields/FieldsArchiveDialog';
import { ScopeTreePicker } from '@/components/scope/ScopeTreePicker';
import {
  ScopeIcon,
  fmtScopeArea,
  scopeArableHa,
  topLevelScopeName,
  type AvailableScopes,
  type ScopeInfo,
  type ScopeSelection,
} from '@/components/scope/ScopeTree';
import {
  fetchScopeAvailable,
  invalidateScopeAvailable,
} from '@/lib/http/sharedQueries';
import { useScopeChanged } from '@/lib/scopeEvent';
import {
  ALL_COLUMN_KEYS,
  COL_LABELS,
  DEFAULT_VISIBLE,
  buildFieldColumns,
  type ColKey,
  type FieldItem,
} from '@/components/fields/FieldsTable';
import {
  buildSowingStatusOptions,
  sowingStatusFilterLabel,
  type SowingStatusFacet,
} from '@/lib/sowings/statusFilter';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { differsAfterRounding } from '@/lib/numberFmt';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import { capBatchSelection, batchLimitNotice } from '@/lib/bulk/batchLimits';
// Хук вынесен из этого файла в общий модуль под карточку хозяйства (AK-746):
// там та же беда с запросом на каждую букву. Логика не менялась.
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import { archiveFieldsBatch, selectionStats } from './archiveBatch';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { isSeasonCompleted } from '@/lib/season/computedStatus';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import { useIsMobile } from '@/lib/useBreakpoint';
import { useSetupStatus } from '@/lib/onboarding/useSetupStatus';
import { fieldsEmptyKind, type FieldsFilterState } from './emptyState';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import {
  fieldsFacetParams,
  fieldsListSearch,
  fieldsQueryParams,
  nextFieldsSearch,
  type FieldsListSearch,
} from './listSearch';

const PAGE = 100;

interface FieldsResponse {
  total: number;
  items: FieldItem[];
  /**
   * Числа ВСЕЙ выборки под фильтром (AK-750) — считает их сервер тем же
   * агрегатом, что и `total`. Складывать на клиенте нечего: на руках
   * страница (100 строк из 167), и сумма по ней читалась как площадь
   * хозяйства — на этой цифре founder потерял полдня.
   */
  total_area_ha?: number;
  /**
   * Из них пашня, га. Отдельная цифра, потому что одна «площадь» на этом
   * экране спорила с 13 958 га на экранах планирования и в дереве выбора —
   * те давно считают только пашню (UAT founder 25.08: «мы помним, тут
   * только пашня»).
   */
  arable_area_ha?: number;
  /** Сколько РАЗНЫХ хозяйств в выборке (а не в справочнике тенанта). */
  farms_count?: number;
}

/** Числа витрины: приходят с сервера, на клиенте не пересчитываются. */
interface FieldsSummary {
  areaHa: number;
  /**
   * Пашня, га. `null` = сервер не прислал (старый бэкенд в окне выката) —
   * это НЕ «пашни нет»: показать в таком случае «пашня 0» значит соврать
   * уверенно. Пусто ≠ ноль, поэтому тип различает эти два случая.
   */
  arableHa: number | null;
  farmsCount: number;
}

const EMPTY_SUMMARY: FieldsSummary = { areaHa: 0, arableHa: null, farmsCount: 0 };

function summaryOf(r: FieldsResponse): FieldsSummary {
  return {
    areaHa: r.total_area_ha ?? 0,
    arableHa: r.arable_area_ha ?? null,
    farmsCount: r.farms_count ?? 0,
  };
}

const HA_FMT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/**
 * Область экрана: что реально сужает список — и откуда это взялось.
 *
 * `source` нужен не для красоты: от него зависит поведение при смене области
 * в панели (умолчание обязано поехать за ней, выбор человека — нет) и подпись
 * рядом с контролом.
 */
type ScopeSource = 'screen' | 'panel' | 'fallback';

interface Brigade {
  id: string;
  farm_id: string;
  name: string;
}

const LS_COL_KEY = 'fieldsTableCols.v1';

export function FieldsListPage() {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
  // Единственный источник правды по фильтрам — адрес. Локальной копии нет
  // намеренно: две копии разъезжаются ровно в тот момент, когда пользователь
  // нажимает «назад».
  const search = useMemo(() => fieldsListSearch(rawSearch), [rawSearch]);
  const isMobile = useIsMobile();
  // AK-363: создание поля / импорт — структурные writes (backend
  // require_structure_write → ресурс `structure`). Нет права `structure:c` →
  // скрываем обе кнопки (destructive-к-структуре действие, не дизейбл).
  const canCreateField = useCan(PERM.fieldWrite, 'c');

  const [items, setItems] = useState<FieldItem[]>([]);
  const [total, setTotal] = useState(0);
  /**
   * Сервер отдал пустую страницу — догружать больше нечего.
   *
   * Отдельный флаг, а не `items.length >= total`: счётчик и строки приходят
   * из РАЗНЫХ запросов и в редких случаях расходятся; тогда список до
   * `total` не дотягивает никогда и просит следующую страницу без конца.
   */
  const [exhausted, setExhausted] = useState(false);
  /** Числа витрины — приходят агрегатом с бэкенда, не считаются тут. */
  const [summary, setSummary] = useState<FieldsSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true); // initial
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brigades, setBrigades] = useState<Brigade[]>([]);
  /**
   * `/scope/available` — тот же источник и тот же кэш, что у панели в шапке
   * (`sharedQueryKeys.scopeAvailable`): дерево «компания → кластеры →
   * хозяйства» с гектарами, уже суженное правами. Плоский `/farms` из этого
   * экрана ушёл — он отдавал список без кластеров, без площадей и без учёта
   * того, что человеку видно.
   */
  const [scopeData, setScopeData] = useState<AvailableScopes | null>(null);
  /**
   * Область уже известна (или спросить её не удалось).
   *
   * Пока не известна — список НЕ запрашиваем: иначе первый заход всегда
   * начинается с самого дорогого запроса «все поля холдинга», который через
   * мгновение отменяется и переигрывается под хозяйство панели.
   */
  const [scopeReady, setScopeReady] = useState(false);

  /**
   * Правка фильтра = навигация с `replace`. Именно `replace`, а не push:
   * набор фильтров — не шаг истории, иначе «назад» из карточки поля
   * отматывал бы по одному нажатию на каждую набранную букву.
   */
  const setFilters = useCallback(
    (patch: Partial<FieldsListSearch>) => {
      navigate({
        to: '/fields',
        search: (prev) => nextFieldsSearch(prev as FieldsListSearch, patch),
        replace: true,
      });
    },
    [navigate],
  );

  const searchText = search.search ?? '';
  const brigadeFilter = search.brigade_id;
  const statusFilter = search.sowing_status;
  const cropFilter = search.crop ?? '';
  const irrigationFilter = search.has_irrigation;
  /**
   * Режим «Архив» (AK-750). Условие founder к политике архивации: «если
   * понадобится — добуду из архива». До этого добывать было неоткуда: фронт
   * архив не показывал вовсе.
   *
   * Это ОТДЕЛЬНЫЙ режим экрана, а не «показать ещё и архивные»: 67 архивных
   * дублей среди 7 547 живых полей человек глазами не найдёт.
   */
  const archivedMode = search.archived === 1;

  // ── Гибрид «панель задаёт умолчание, экран может отклониться» (AK-772) ───
  const available: readonly ScopeInfo[] = scopeData?.available ?? [];
  const companyScope = available.find((s) => s.scope_type === 'company') ?? null;
  // «Вся компания» — та же строка дерева, что видна в панели: у неё свой
  // `scope_id`, поэтому сравнивать с самодельным `null` нельзя (галочка не
  // встанет). `null` остаётся запасным вариантом для кабинетов без
  // company-строки в `/scope/available`.
  const wholeCompany: ScopeSelection = companyScope
    ? { scope_type: 'company', scope_id: companyScope.scope_id }
    : { scope_type: 'company', scope_id: null };

  // Умолчание берём из панели, но только когда оно реально сужает список:
  // компания / холдинг / тенант означают «всё», и фильтровать по ним нечего.
  const panelCurrent = scopeData?.current ?? null;
  const panelScope: ScopeSelection | null =
    panelCurrent &&
    (panelCurrent.scope_type === 'farm' || panelCurrent.scope_type === 'cluster') &&
    panelCurrent.scope_id
      ? { scope_type: panelCurrent.scope_type, scope_id: panelCurrent.scope_id }
      : null;

  // Выбор экрана живёт в адресе — поэтому переживает «назад», перезагрузку и
  // пересылку ссылки, и поэтому же он сильнее панели при монтировании:
  // присланный `?farm_id=` открывает то хозяйство, о котором ссылка (тот же
  // порядок победителей, что у карты, AK-296).
  const screenScope: ScopeSelection | null = search.farm_id
    ? { scope_type: 'farm', scope_id: search.farm_id }
    : search.cluster_id
      ? { scope_type: 'cluster', scope_id: search.cluster_id }
      : search.whole_company
        ? wholeCompany
        : null;

  const scopeSource: ScopeSource = screenScope
    ? 'screen'
    : panelScope
      ? 'panel'
      : 'fallback';
  const selection: ScopeSelection = screenScope ?? panelScope ?? wholeCompany;
  const farmFilter =
    selection.scope_type === 'farm' ? (selection.scope_id ?? undefined) : undefined;
  const clusterFilter =
    selection.scope_type === 'cluster' ? (selection.scope_id ?? undefined) : undefined;

  /**
   * Что уходит на сервер: адрес плюс подставленное умолчание области.
   *
   * ОДИН объект на список, счётчики статусов, `resetKey` и пустые состояния —
   * иначе «что показано в фильтре» и «что попало в выборку» разъедутся, а это
   * ровно тот класс расхождений, из-за которого экран перестают читать.
   */
  const effectiveSearch: FieldsListSearch = useMemo(
    () => ({ ...search, farm_id: farmFilter, cluster_id: clusterFilter }),
    [search, farmFilter, clusterFilter],
  );

  // Подпись контрола — имя выбранного узла дерева. Ссылка может привести на
  // хозяйство, которого нет в доступных: показываем это словами, а не пустой
  // кнопкой (и не подставляем id — человеку он ничего не говорит).
  const selectedScope =
    available.find(
      (s) =>
        s.scope_type === selection.scope_type &&
        (s.scope_id ?? null) === (selection.scope_id ?? null),
    ) ?? null;
  const companyLabel = topLevelScopeName(available);
  const scopeLabel =
    selection.scope_type === 'company'
      ? companyLabel
      : (selectedScope?.name ?? 'Область не найдена');

  const pushSearchText = useCallback(
    (value: string) => setFilters({ search: value || undefined }),
    [setFilters],
  );
  const pushCropFilter = useCallback(
    (value: string) => setFilters({ crop: value || undefined }),
    [setFilters],
  );
  const [searchDraft, setSearchDraft] = useTextFilterDraft(searchText, pushSearchText);
  const [cropDraft, setCropDraft] = useTextFilterDraft(cropFilter, pushCropFilter);
  // AK-711: пункты фильтра статуса = то, что реально есть в выборке.
  // `null` — ещё не считали (или не смогли): пунктов нет, но «Все статусы»
  // работает — фильтр не блокируем из-за витрины.
  const [statusFacets, setStatusFacets] = useState<SowingStatusFacet[] | null>(null);
  const [statusFacetsLoading, setStatusFacetsLoading] = useState(false);
  /** Счёт статусов не удался — это НЕ то же самое, что «ещё не считали». */
  const [statusFacetsFailed, setStatusFacetsFailed] = useState(false);
  // Ключ выборки, для которой посчитаны фасеты. Считаем ЛЕНИВО, по открытию
  // списка: запрос идёт по всем полям тенанта (LATERAL на занятие), и платить
  // им за каждый заход на «Поля» тем, кто фильтром не пользуется, — незачем.
  const facetsForKey = useRef<string | null>(null);
  /** Актуальный ключ выборки — чтобы ответ «вдогонку» не подменил счётчики. */
  const latestFacetsKey = useRef<string>('');

  const reqSeq = useRef(0);
  const offsetRef = useRef(0);

  const [visibleCols, setVisibleCols] = useState<ColKey[]>(DEFAULT_VISIBLE);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_COL_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ColKey[];
        const clean = parsed.filter((k) =>
          (ALL_COLUMN_KEYS as readonly string[]).includes(k),
        );
        if (clean.length) setVisibleCols(clean as ColKey[]);
      }
    } catch {
      /* ignore */
    }
  }, []);
  const toggleCol = (key: ColKey, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...visibleCols, key]))
      : visibleCols.filter((k) => k !== key);
    setVisibleCols(next);
    localStorage.setItem(LS_COL_KEY, JSON.stringify(next));
  };

  // AK-208: колонка «Уклон» скрывается на узких экранах (<768px) — экономим
  // ширину для ключевых колонок. Скрыта → terrain-JOIN на бэкенде не нужен.
  const effectiveCols = useMemo(
    () => (isMobile ? visibleCols.filter((k) => k !== 'slope') : visibleCols),
    [isMobile, visibleCols],
  );
  const wantTerrain = effectiveCols.includes('slope');

  // AK-208: серверная сортировка (по slope). null = дефолт (по имени на бэке).
  const [sort, setSort] = useState<SortState | null>(null);
  // Уклон недоступен для сортировки → сбрасываем sort, чтобы не слать
  // ?sort=slope без include=terrain (бэкенд тихо фолбэкнет, но чистим состояние).
  useEffect(() => {
    if (sort?.field === 'slope' && !wantTerrain) setSort(null);
  }, [sort, wantTerrain]);

  // Load reference data once. Отделения — единственный плоский справочник,
  // который экрану всё ещё нужен: кластеры и хозяйства приезжают деревом из
  // `/scope/available` вместе с гектарами и правами.
  useEffect(() => {
    api
      .get<Brigade[]>('/api/v1/brigades')
      .then(setBrigades)
      .catch(() => {
        /* tolerate */
      });
  }, []);

  /**
   * Область панели. Отказ — не повод не показать поля: экран продолжает жить
   * без умолчания (как жил до AK-772), поэтому `scopeReady` взводится и на
   * ошибке.
   */
  const loadScope = useCallback(() => {
    fetchScopeAvailable<AvailableScopes>()
      .then((d) => setScopeData(d && typeof d === 'object' && 'current' in d ? d : null))
      .catch(() => setScopeData(null))
      .finally(() => setScopeReady(true));
  }, []);

  useEffect(() => {
    loadScope();
  }, [loadScope]);

  /**
   * Переключили область в правой панели — экран обязан поехать за ней.
   *
   * Сбрасываем выбор ЭКРАНА: раз человек переключил область глобально,
   * умолчание списка снова обязано идти от неё, а не от прошлой ручной
   * настройки (тот же приём, что в аналитике). Кэш `/scope/available` при
   * этом протух — `current` в нём прежний.
   */
  useScopeChanged(() => {
    invalidateScopeAvailable();
    loadScope();
    navigate({
      to: '/fields',
      search: (prev) =>
        nextFieldsSearch(prev as FieldsListSearch, {
          farm_id: undefined,
          cluster_id: undefined,
          brigade_id: undefined,
          whole_company: undefined,
        }),
      replace: true,
    });
  });

  // filter signature — changing it resets the list + scroll
  const resetKey = useMemo(
    () =>
      JSON.stringify([
        // Именно effectiveSearch: смена области в панели меняет выборку так
        // же, как правка фильтра руками, — значит список обязан перечитаться
        // и прокрутка сброситься.
        effectiveSearch,
        // Смена сортировки/наличия terrain-колонки = новый набор с сервера.
        sort ? `${sort.field}:${sort.order}` : '',
        wantTerrain ? 'terrain' : '',
      ]),
    [effectiveSearch, sort, wantTerrain],
  );

  // AK-208: `include=terrain` — только когда колонка «Уклон» видна.
  const buildQs = useCallback(
    (offset: number) =>
      fieldsQueryParams(effectiveSearch, { limit: PAGE, offset, wantTerrain, sort }),
    [effectiveSearch, wantTerrain, sort],
  );

  const loadFirst = useCallback(() => {
    const seq = ++reqSeq.current;
    offsetRef.current = 0;
    setLoading(true);
    // Новая выборка отменяет догрузку прошлой: её ответ уже не наш, и флаг
    // «подгружаем» обязан погаснуть здесь — иначе он остаётся включённым
    // навсегда (ответ придёт с чужим seq и до своего `finally` не дойдёт).
    setLoadingMore(false);
    setExhausted(false);
    setError(null);
    api
      .get<FieldsResponse>(`/api/v1/fields?${buildQs(0)}`)
      .then((r) => {
        if (seq !== reqSeq.current) return;
        setItems(r.items);
        setTotal(r.total);
        setSummary(summaryOf(r));
        offsetRef.current = r.items.length;
      })
      .catch(() => {
        if (seq !== reqSeq.current) return;
        setError('Не удалось загрузить поля');
        setItems([]);
        setTotal(0);
        setSummary(EMPTY_SUMMARY);
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [buildQs]);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || exhausted || offsetRef.current >= total) return;
    const seq = reqSeq.current;
    const offset = offsetRef.current;
    setLoadingMore(true);
    setError(null);
    api
      .get<FieldsResponse>(`/api/v1/fields?${buildQs(offset)}`)
      .then((r) => {
        if (seq !== reqSeq.current) return;
        setItems((prev) => [...prev, ...r.items]);
        setTotal(r.total);
        setSummary(summaryOf(r));
        offsetRef.current = offset + r.items.length;
        // Сервер отдал пустую страницу — больше брать нечего, что бы ни
        // говорил `total`. Без этого расхождение счётчика и строк (а оно
        // возможно: считает и отдаёт разная выборка) превращается в
        // бесконечную карусель запросов у нижней кромки списка.
        if (r.items.length === 0) setExhausted(true);
      })
      .catch(() => {
        if (seq !== reqSeq.current) return;
        setError('Не удалось догрузить');
      })
      .finally(() => {
        // Гард по seq — и на данные, и на индикатор: безусловное гашение
        // погасило бы «подгружаем» УЖЕ ИДУЩЕЙ новой догрузки, если устаревший
        // ответ приходит после её старта. Устаревшую догрузку отменяет тот,
        // кто её обесценил, — `loadFirst` (см. сброс флага там).
        if (seq === reqSeq.current) setLoadingMore(false);
      });
  }, [loadingMore, loading, exhausted, total, buildQs]);

  const retry = useCallback(() => {
    setError(null);
    if (items.length === 0) loadFirst();
    else loadMore();
  }, [items.length, loadFirst, loadMore]);

  useEffect(() => {
    // Пока не знаем область панели — не спрашиваем поля: первый запрос ушёл
    // бы за всем холдингом и тут же был бы отменён (AK-772).
    if (!scopeReady) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, scopeReady]);

  const brigadeOptions = useMemo(
    () =>
      brigades
        .filter((b) => !farmFilter || b.farm_id === farmFilter)
        .map((b) => ({ value: b.id, label: b.name })),
    [brigades, farmFilter],
  );

  // Сезонная линза: в завершённом сезоне занятие «Запланировано» показывается
  // как «Не реализован» (канон v3 §5 + ADR-063 — данные не мутируем).
  const { season } = useActiveSeason();
  const seasonCompleted = season
    ? isSeasonCompleted({
        startsOn: season.startsOn,
        endsOn: season.endsOn,
        year: season.year,
        isCurrent: season.isCurrent,
      })
    : false;
  // AK-711 — фасеты статусов занятия: те же фильтры, что у списка, МИНУС сам
  // статус (иначе, выбрав один пункт, теряешь из выпадашки остальные).
  const facetsKey = useMemo(
    () => `${fieldsFacetParams(effectiveSearch)}|${season?.seasonId ?? ''}`,
    [effectiveSearch, season?.seasonId],
  );
  // Сменились остальные фильтры → прежние счётчики врут, гасим их.
  useEffect(() => {
    latestFacetsKey.current = facetsKey;
    if (facetsForKey.current !== null && facetsForKey.current !== facetsKey) {
      facetsForKey.current = null;
      setStatusFacets(null);
      setStatusFacetsFailed(false);
    }
  }, [facetsKey]);

  const loadStatusFacets = useCallback(() => {
    if (facetsForKey.current === facetsKey || statusFacetsLoading) return;
    const key = facetsKey;
    const qs = fieldsFacetParams(effectiveSearch);
    setStatusFacetsLoading(true);
    setStatusFacetsFailed(false);
    api
      .get<{ items: SowingStatusFacet[] }>(`/api/v1/fields/sowing-status-facets?${qs}`)
      .then((r) => {
        // Пока считали, пользователь мог сузить хозяйство — ответ не про эту
        // выборку, показывать его нельзя.
        if (latestFacetsKey.current !== key) return;
        facetsForKey.current = key;
        setStatusFacets(r.items);
      })
      .catch(() => {
        // Фасеты — витрина фильтра, а не сам фильтр: их падение не должно
        // ронять экран. Пунктов не будет, «Все статусы» продолжит работать.
        if (latestFacetsKey.current !== key) return;
        facetsForKey.current = null;
        setStatusFacets(null);
        setStatusFacetsFailed(true);
      })
      .finally(() => setStatusFacetsLoading(false));
  }, [facetsKey, statusFacetsLoading, effectiveSearch]);

  const statusOptions = useMemo(
    () =>
      buildSowingStatusOptions(statusFacets, {
        seasonCompleted,
        keepValue: statusFilter,
      }),
    [statusFacets, seasonCompleted, statusFilter],
  );

  // ── Чистка списка: отметил чекбоксами → «В архив» (AK-750) ──────────────
  //
  // Право то же, что у одиночной архивации в карточке поля (бэкенд гейтит
  // структурную запись). Нет права — чекбоксов нет вовсе: упереться в 403 на
  // последнем шаге хуже, чем не увидеть контрол.
  const canArchive = useCan(PERM.fieldWrite, 'u');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Сменили фильтр — прежний выбор относится к полям, которых на экране уже
  // нет. Держать его = архивировать вслепую.
  useEffect(() => {
    setSelected(new Set());
    setArchiveOpen(false);
  }, [resetKey]);

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const stats = useMemo(() => selectionStats(items, selected), [items, selected]);

  const runArchive = async (reason: string) => {
    setArchiving(true);
    const ids = items.filter((r) => selected.has(r.id)).map((r) => r.id);
    try {
      // ОДИН запрос на всю пачку и ОДИН итог словами — с именами полей и
      // причинами отказов (канон «Массовые действия»).
      const outcome = await archiveFieldsBatch(ids, reason, items);
      toast[outcome.kind](outcome.message);
      if (outcome.archivedCount > 0) {
        setSelected(new Set());
        // Поля ушли с карты — сбрасываем chip-кеш, как одиночная архивация.
        invalidateMapInit();
      }
    } finally {
      // Перечитываем список ВСЕГДА, а не только на успехе: при частичном
      // отказе сервер уже изменился, и молчащий экран поверх записи — худшее
      // из состояний.
      setArchiving(false);
      setArchiveOpen(false);
      loadFirst();
    }
  };

  /**
   * Восстановление поля из архива — по одному (AK-750).
   *
   * Пакетной ручки восстановления на бэкенде НЕТ (есть только
   * `POST /fields/{id}/restore`, AK-347), и придумывать её здесь, «под
   * кнопку», нельзя: восстановление тянет за собой каскад посевов, а
   * семантику пакета для него никто не утверждал. Поэтому строка за строкой,
   * с обычным подтверждением делом — самим списком.
   */
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const restoreField = useCallback(
    async (row: FieldItem) => {
      setRestoringId(row.id);
      try {
        await api.post(`/api/v1/fields/${row.id}/restore`, { restore_sowings: true });
        toast.success(`Поле «${row.name}» восстановлено`);
        invalidateMapInit();
      } catch (err) {
        toast.error(
          err instanceof ApiError
            ? `Не удалось восстановить: ${err.detail}`
            : `Не удалось восстановить поле «${row.name}»`,
        );
      } finally {
        setRestoringId(null);
        // Перечитываем ВСЕГДА: при успехе поле ушло из архивной выборки, при
        // отказе экран обязан показать факт, а не прошлое состояние.
        loadFirst();
      }
    },
    [loadFirst],
  );

  const columns = useMemo(() => {
    const cols = buildFieldColumns(effectiveCols, { seasonCompleted });
    if (archivedMode) {
      // В архиве другой разговор: не «что тут растёт», а «когда и почему
      // убрали» и «вернуть». Массовых действий здесь нет — см. restoreField.
      const archivedCols: VirtualColumn<FieldItem>[] = [
        {
          key: '__archived_at',
          header: 'Архивировано',
          width: 'minmax(160px, 1fr)',
          cell: (r) => (
            <span
              className="text-muted-foreground"
              title={r.archive_reason ?? undefined}
            >
              {formatRuDateNumeric(r.archived_at)}
              {r.archive_reason ? ` · ${r.archive_reason}` : ''}
            </span>
          ),
        },
      ];
      if (canArchive) {
        archivedCols.push({
          key: '__restore',
          header: '',
          width: '150px',
          cell: (r) => (
            <Button
              variant="outline"
              size="sm"
              disabled={restoringId === r.id}
              onClick={() => restoreField(r)}
            >
              <ArchiveRestore className="h-4 w-4 mr-2" />
              {restoringId === r.id ? 'Возвращаю…' : 'Восстановить'}
            </Button>
          ),
        });
      }
      return [...cols, ...archivedCols];
    }
    if (!canArchive) return cols;
    const loadedIds = items.map((r) => r.id);
    const allChecked = loadedIds.length > 0 && loadedIds.every((id) => selected.has(id));
    const someChecked = !allChecked && loadedIds.some((id) => selected.has(id));
    const selectColumn: VirtualColumn<FieldItem> = {
      key: '__select',
      width: '44px',
      // «Все» — это ПОКАЗАННЫЕ поля, а не все 7547 в тенанте: список
      // бесконечный, и обещать выбор незагруженного нельзя.
      header: (
        <Checkbox
          checked={allChecked ? true : someChecked ? 'indeterminate' : false}
          disabled={loadedIds.length === 0}
          onCheckedChange={(v) =>
            // Предел ОДНОЙ пачки общий с карточкой хозяйства: ручка архивации
            // принимает 200, а бесконечный список догружает больше — без
            // обрезки один клик по «выбрать всё» давал бы 422.
            setSelected(v === true ? new Set(capBatchSelection(loadedIds)) : new Set())
          }
          aria-label="Выбрать показанные поля"
        />
      ),
      cell: (r) => (
        <Checkbox
          checked={selected.has(r.id)}
          onCheckedChange={() => toggleRow(r.id)}
          aria-label={`Поле ${r.name}`}
        />
      ),
    };
    return [selectColumn, ...cols];
  }, [
    effectiveCols,
    seasonCompleted,
    canArchive,
    items,
    selected,
    toggleRow,
    archivedMode,
    restoringId,
    restoreField,
  ]);

  // AK-584: «полей вообще нет» и «фильтр не совпал» — разные тупики и разные
  // выходы из них. Логика развилки — в `emptyState.ts` (там же тесты).
  const filterState: FieldsFilterState = {
    searchText,
    farmFilter,
    clusterFilter,
    brigadeFilter,
    statusFilter,
    cropFilter,
    irrigationFilter:
      irrigationFilter === undefined ? undefined : String(irrigationFilter),
    archivedMode,
  };
  // `farmFilter`/`clusterFilter` здесь — уже ЭФФЕКТИВНЫЕ (в них может стоять
  // умолчание из шапки), поэтому пустой ответ под областью читается как «не
  // подошло», а не как «полей нет вовсе». Счётчики тенанта всё равно берём из
  // /scope/available (они от активной области не зависят), чтобы не звать
  // «нарисуйте первое поле» клиента с 200 полями, стоящего на пустом
  // хозяйстве.
  const { counts: tenantCounts } = useSetupStatus();
  const emptyKind = fieldsEmptyKind({
    loading,
    error,
    total,
    filters: filterState,
    tenantHasFields: tenantCounts ? tenantCounts.fields > 0 : null,
  });

  // Сброс = пустой адрес. Черновики текстовых полей следуют за адресом сами
  // (см. `useTextFilterDraft`), отдельно их гасить не нужно.
  const resetFilters = () => {
    navigate({ to: '/fields', search: {}, replace: true });
  };

  return (
    <AppShell>
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground mb-1">Поля</h1>
            <p className="text-sm text-muted-foreground">
              Всего: {total.toLocaleString('ru-RU')} · загружено:{' '}
              {items.length}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canCreateField && (
              <>
                <Button onClick={() => navigate({ to: '/map', search: { addField: 1 } })}>
                  <Plus className="h-4 w-4 mr-2" />
                  {isMobile ? 'Создать' : 'Создать поле'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({ to: '/map', search: { addField: 1, import: 1 } })
                  }
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {isMobile ? 'Импорт' : 'Импорт из файла'}
                </Button>
              </>
            )}
            {/* SA-01 фаза 1 — точка входа в «Закрытие сезона». Сезонное
                действие, поэтому кнопка тут, а не отдельным пунктом сайдбара. */}
            <Button variant="outline" onClick={() => navigate({ to: '/season-closing' })}>
              {isMobile ? 'Закрыть сезон' : 'Закрытие сезона'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">Всего полей</div>
              <div className="text-2xl font-semibold mt-1">
                {total.toLocaleString('ru-RU')}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              {/* Площадь ПОД ФИЛЬТРОМ, а не «сколько успели подгрузить».
                  Две цифры, как в дереве выбора (AK-752): первая — пашня,
                  ею меряют планирование и экономику; вторая — вся земля
                  выборки. Одна общая цифра спорила с 13 958 га на соседних
                  экранах, и расхождение читалось как «система врёт».
                  В режиме архива пашни не бывает по определению (поле
                  выведено из оборота) — там показываем одну цифру, иначе
                  карточка честно врала бы «пашня 0». */}
              <div className="text-xs text-muted-foreground">Площадь, га</div>
              <div
                data-testid="fields-total-area"
                className="mt-1 flex items-baseline gap-2"
                title={
                  archivedMode
                    ? 'Сумма площадей архивных полей выборки'
                    : 'Пашня — активные поля под севооборотом; всего — вся земля выборки'
                }
              >
                {archivedMode ||
                summary.arableHa === null ||
                !differsAfterRounding(summary.arableHa, summary.areaHa) ? (
                  <span className="text-2xl font-semibold">
                    {HA_FMT.format(summary.areaHa)}
                  </span>
                ) : (
                  <>
                    <span className="text-2xl font-semibold">
                      пашня {HA_FMT.format(summary.arableHa)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      всего {HA_FMT.format(summary.areaHa)}
                    </span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" /> Хозяйств
              </div>
              {/* По ВЫБОРКЕ, а не по справочнику тенанта: при фильтре на одно
                  хозяйство здесь стояло 67 — число хозяйств всей компании
                  (UAT founder 25.08). */}
              <div data-testid="fields-farms-count" className="text-2xl font-semibold mt-1">
                {summary.farmsCount.toLocaleString('ru-RU')}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="flex min-h-0 flex-1 flex-col">
          <CardContent className="flex min-h-0 flex-1 flex-col p-4 md:p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Поиск по названию"
                  value={searchDraft}
                  onChange={(e) => {
                    setSearchDraft(e.target.value);
                  }}
                />
              </div>

              {/* Хозяйство и кластер — ОДИН штатный контрол с деревом и
                  поиском (AK-745: «этот ux выбора хозяйства надо заменить
                  штатным, который у нас был с правой стороны»). Две плоские
                  выпадашки на шестидесяти хозяйствах требовали помнить, в
                  каком кластере искать; дерево показывает это сразу и вместе
                  с гектарами. Выбор ЛОКАЛЬНЫЙ: `POST /scope/set` отсюда не
                  зовётся, область всей системы не меняется. */}
              <div className="flex items-center gap-2" data-testid="fields-scope-picker">
                <ScopeTreePicker
                  available={available}
                  value={selection}
                  onPick={(s) => {
                    if (s.scope_type === 'company') {
                      setFilters({ whole_company: 1 });
                      return;
                    }
                    if (s.scope_type === 'cluster') {
                      // Каскад (сброс хозяйства и отделения) — внутри
                      // `nextFieldsSearch`: ДВЕ навигации подряд затирали бы
                      // друг друга, адрес получал бы только последнюю.
                      setFilters({ cluster_id: s.scope_id ?? undefined });
                      return;
                    }
                    setFilters({ farm_id: s.scope_id ?? undefined });
                  }}
                  companyLabel={companyLabel}
                  headerName={companyLabel}
                  triggerLabel={scopeLabel}
                  triggerIcon={<ScopeIcon type={selection.scope_type} className="h-4 w-4 shrink-0" />}
                  triggerHint={
                    selectedScope ? fmtScopeArea(scopeArableHa(selectedScope)) || undefined : undefined
                  }
                  sheetTitle="Выбор области: компания, кластер или хозяйство"
                  sheetDescription="Фильтр этого экрана. Активную область всей системы не меняет."
                  disabled={!scopeReady}
                />
                {/* Откуда взялась область — иначе «почему тут только Аргамак»
                    остаётся загадкой, а вернуться к умолчанию нечем. */}
                {scopeSource === 'panel' ? (
                  <span
                    data-testid="fields-scope-from-panel"
                    className="text-xs text-muted-foreground"
                  >
                    как в шапке
                  </span>
                ) : (
                  panelScope && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="fields-scope-back-to-panel"
                      onClick={() =>
                        setFilters({
                          farm_id: undefined,
                          cluster_id: undefined,
                          brigade_id: undefined,
                          whole_company: undefined,
                        })
                      }
                    >
                      Как в шапке
                    </Button>
                  )
                )}
              </div>

              <Select
                value={brigadeFilter ?? '__all__'}
                onValueChange={(v) => {
                  setFilters({ brigade_id: v === '__all__' ? undefined : v });
                }}
                disabled={!farmFilter && brigadeOptions.length > 50}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="Отделение" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Все отделения</SelectItem>
                  {brigadeOptions.map((b) => (
                    <SelectItem key={b.value} value={b.value}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Статус ЗАНЯТИЯ и культура — про живое поле. В архиве занятия
                  тоже архивные, и фильтровать по ним нечего: показывали бы
                  пустые выборки и врущие счётчики. */}
              {!archivedMode && (
              <Select
                value={statusFilter ?? '__all__'}
                onValueChange={(v) => {
                  setFilters({ sowing_status: v === '__all__' ? undefined : v });
                }}
                onOpenChange={(open) => {
                  if (open) loadStatusFacets();
                }}
              >
                <SelectTrigger className="w-[170px]">
                  {/* В кнопке — только слово: счётчик полезен при ВЫБОРЕ, а в
                      узком триггере он вытеснял бы сам статус многоточием. */}
                  <SelectValue placeholder="Статус посева">
                    {sowingStatusFilterLabel(statusFilter, { seasonCompleted }) ??
                      'Все статусы'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Все статусы</SelectItem>
                  {statusFacets === null && (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      {statusFacetsFailed
                        ? 'Статусы посчитать не удалось'
                        : 'Считаем статусы…'}
                    </div>
                  )}
                  {statusFacets !== null && statusOptions.length === 0 && (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      В этой выборке нет полей
                    </div>
                  )}
                  {statusOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <span className="flex w-full items-center justify-between gap-3">
                        <span>{o.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {o.count.toLocaleString('ru-RU')}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              )}

              {!archivedMode && (
              <Input
                placeholder="Культура"
                value={cropDraft}
                onChange={(e) => {
                  setCropDraft(e.target.value);
                }}
                className="w-[160px]"
              />
              )}

              <Select
                value={
                  irrigationFilter === undefined
                    ? '__all__'
                    : irrigationFilter
                      ? 'true'
                      : 'false'
                }
                onValueChange={(v) => {
                  // В адресе орошение живёт БУЛЕВЫМ (`?has_irrigation=true`),
                  // а не строкой — см. грабля JSON в `./listSearch`.
                  setFilters({
                    has_irrigation: v === '__all__' ? undefined : v === 'true',
                  });
                }}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Орошение" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Любое</SelectItem>
                  <SelectItem value="true">Есть</SelectItem>
                  <SelectItem value="false">Нет</SelectItem>
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Колонки">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Колонки</DropdownMenuLabel>
                  {ALL_COLUMN_KEYS.map((k) => (
                    <DropdownMenuCheckboxItem
                      key={k}
                      checked={visibleCols.includes(k)}
                      onCheckedChange={(v) => toggleCol(k, !!v)}
                    >
                      {COL_LABELS[k]}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Статус ПОЛЯ, а не занятия — поэтому отдельный переключатель,
                  а не пункт в выпадашке статусов посева. Условие founder к
                  политике архивации: «если понадобится — добуду из архива»;
                  до этого добывать было неоткуда. Режим живёт в адресе
                  (`?archived=1`), поэтому переживает «назад» и пересылается
                  ссылкой вместе с остальным фильтром. */}
              <div className="flex items-center gap-2 ml-auto">
                <Switch
                  id="fields-archived-mode"
                  data-testid="fields-archived-toggle"
                  checked={archivedMode}
                  onCheckedChange={(on) =>
                    setFilters({
                      archived: on ? 1 : undefined,
                      // Фильтры живого занятия в архиве скрыты — уносим их из
                      // адреса, иначе они молча продолжали бы сужать выборку.
                      sowing_status: undefined,
                      crop: undefined,
                    })
                  }
                />
                <Label htmlFor="fields-archived-mode" className="text-sm">
                  Архивные
                </Label>
              </div>
            </div>

            {archivedMode && (
              <p
                data-testid="fields-archived-hint"
                className="mb-4 text-sm text-muted-foreground"
              >
                Показаны только архивные поля. Они не видны на карте и в
                планировании; «Восстановить» возвращает поле вместе с его
                занятиями.
              </p>
            )}

            {/* Панель выбора — появляется только когда что-то отмечено:
                постоянная строка с нулями шумит, а действие тут одно. */}
            {canArchive && !archivedMode && stats.count > 0 && (
              <div
                data-testid="fields-selection-bar"
                className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3"
              >
                <span className="text-sm text-foreground">
                  Выбрано: {stats.count} · {Math.round(stats.areaHa).toLocaleString('ru-RU')} га
                </span>
                {/* Обрезали выбор до предела пачки — объясняем словами, тем же
                    текстом, что и в карточке хозяйства. */}
                {batchLimitNotice(stats.count, items.length) && (
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid="batch-limit-notice"
                  >
                    {batchLimitNotice(stats.count, items.length)}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(new Set())}
                  >
                    Снять выбор
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    data-testid="fields-archive-open"
                    onClick={() => setArchiveOpen(true)}
                  >
                    <Archive className="h-4 w-4 mr-2" />В архив ({stats.count})
                  </Button>
                </div>
              </div>
            )}

            {emptyKind === 'no-fields' ? (
              <div
                data-testid="fields-empty-no-fields"
                className="flex min-h-0 flex-1 items-center justify-center"
              >
                <div className="flex max-w-md flex-col items-center rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-center">
                  <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-muted-foreground">
                    <MapPin className="h-6 w-6" />
                  </span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Здесь появятся ваши поля
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Обведите поле на карте или загрузите файл с готовыми
                    границами — .shp, .kml, .geojson.
                  </p>
                  {canCreateField && (
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button
                        variant="accent-brand"
                        data-testid="fields-empty-draw"
                        onClick={() =>
                          navigate({ to: '/map', search: { addField: 1 } })
                        }
                      >
                        <Plus className="h-4 w-4" />
                        Нарисовать поле на карте
                      </Button>
                      <Button
                        variant="outline"
                        data-testid="fields-empty-import"
                        onClick={() =>
                          navigate({
                            to: '/map',
                            search: { addField: 1, import: 1 },
                          })
                        }
                      >
                        <FileDown className="h-4 w-4" />
                        Загрузить файл
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : emptyKind === 'no-matches' ? (
              <div
                data-testid="fields-empty-no-matches"
                className="flex min-h-0 flex-1 items-center justify-center"
              >
                <div className="flex max-w-md flex-col items-center rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-center">
                  <h3 className="text-base font-semibold text-foreground">
                    Здесь ничего не подошло
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Поля в системе есть, но не с такими условиями. Проверьте
                    фильтры и выбранное в шапке хозяйство.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    data-testid="fields-empty-reset-filters"
                    onClick={resetFilters}
                  >
                    <FilterX className="h-4 w-4" />
                    Сбросить фильтры
                  </Button>
                </div>
              </div>
            ) : (
            <div className="min-h-0 flex-1">
              <VirtualTable
                columns={columns}
                rows={items}
                rowKey={(r) => r.id}
                total={total}
                hasMore={!exhausted && items.length < total}
                loading={loading}
                loadingMore={loadingMore}
                error={error}
                onLoadMore={loadMore}
                onRetry={retry}
                // AK-750: строка целиком больше НЕ навигация. Вход в карточку
                // — ссылка на имени поля (правый клик, «в новой вкладке»,
                // средняя кнопка — всё штатное). Обработчик на строке этого
                // не давал и вдобавок уводил бы со страницы при промахе мимо
                // чекбокса, теряя набранный выбор полей.
                unitLabel="полей"
                emptyText="Полей не найдено"
                restoreKey="fields"
                resetKey={resetKey}
                sort={sort}
                onSortChange={setSort}
                height="100%"
              />
            </div>
            )}
          </CardContent>
        </Card>
      </div>

      <FieldsArchiveDialog
        open={archiveOpen}
        stats={stats}
        pending={archiving}
        onCancel={() => setArchiveOpen(false)}
        onConfirm={runArchive}
      />
    </AppShell>
  );
}

export default FieldsListPage;
